import { Alert, Button, IconButton, Stack, Typography } from '@mui/material'
import { com, kotlin } from '@eidu/form'
import { useState } from 'react'
import { HelpOutline } from '@mui/icons-material'
import FormPage from './FormPage'
import { OtherPagesProvider } from './OtherPagesProvider'
import { useSnackBar } from '../feedback/SnackBarProvider'
import NextOption from './NextOption'
import Page = com.eidu.sharedlib.form.definition.Page
import KtList = kotlin.collections.KtList
import encodeFormContentToYaml = com.eidu.sharedlib.form.encodeFormContentToYaml
import decodeFormContentFromYaml = com.eidu.sharedlib.form.decodeFormContentFromYaml
import { keyOf } from '../../util/withKey'

export type FormContentInputProps = {
  pages: KtList<Page>
  onChange?: (pages: KtList<Page>) => void
  errorMessage?: string
}

const FormContentInput = ({ pages, onChange, errorMessage }: FormContentInputProps) => {
  const { showSnackBar } = useSnackBar()

  const [expandedPageKey, setExpandedPageKey] = useState<string | null>(null)

  const handleInsertPageAtStart = () => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()
    const newPage = new Page('Untitled page', KtList.fromJsArray([]), `page_${Date.now()}`, null)

    const updatedPages = [newPage, ...currentPages]
    onChange(KtList.fromJsArray(updatedPages))
    setExpandedPageKey(keyOf(newPage))
  }

  const handleInsertPageAfter = (stableKey: string) => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()
    const newPage = new Page('Untitled page', KtList.fromJsArray([]), `page_${Date.now()}`, null)

    const index = currentPages.findIndex((page) => keyOf(page) === stableKey)

    const updatedPages = [...currentPages.slice(0, index + 1), newPage, ...currentPages.slice(index + 1)]
    onChange(KtList.fromJsArray(updatedPages))
    setExpandedPageKey(keyOf(newPage))
  }

  const handleDeletePage = (stableKey: string) => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()

    const index = currentPages.findIndex((page) => keyOf(page) === stableKey)

    const updatedPages = [...currentPages.slice(0, index), ...currentPages.slice(index + 1)]
    onChange(KtList.fromJsArray(updatedPages))

    if (stableKey === expandedPageKey) setExpandedPageKey(null)
  }

  const handlePageChange = (currentKey: string, newPage: Page) => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()
    const updatedPages = currentPages.map((page) => (keyOf(page) === keyOf(newPage) ? newPage : page))
    onChange(KtList.fromJsArray(updatedPages))
  }

  const handleExpand = (stableKey: string) => {
    setExpandedPageKey(expandedPageKey === stableKey ? null : stableKey)
  }

  const handleCopyToClipboard = async () => {
    try {
      const yamlContent = encodeFormContentToYaml(pages)
      await navigator.clipboard.writeText(yamlContent)
      showSnackBar('YAML copied to clipboard', 'success')
    } catch (_) {
      showSnackBar('Failed to copy YAML to clipboard', 'error')
    }
  }

  const handlePasteFromClipboard = async () => {
    if (!onChange) return

    try {
      const clipboardText = await navigator.clipboard.readText()

      try {
        const newPages = decodeFormContentFromYaml(clipboardText)
        onChange(newPages)
        showSnackBar('YAML successfully pasted', 'success')
      } catch (_parseError) {
        showSnackBar('Invalid YAML format', 'error')
      }
    } catch (_error) {
      showSnackBar('Failed to access clipboard', 'error')
    }
  }

  const [showInstructions, setShowInstructions] = useState(false)

  return (
    <>
      <Stack
        sx={{ width: '100%', maxWidth: '100%', flexShrink: 1, boxSizing: 'border-box' }}
        direction="row"
        alignItems="start"
        spacing="16px"
      >
        <Stack sx={{ flex: 1, minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
          <Typography variant="h6">
            Form content
            {onChange && (
              <IconButton color="primary" onClick={() => setShowInstructions(!showInstructions)}>
                <HelpOutline sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Typography>

          {showInstructions && (
            <Alert severity="info" onClose={() => setShowInstructions(false)}>
              <p>
                <Typography>
                  You can add, remove, or edit <b>pages</b> in the form.
                </Typography>
                <Typography>Each page can include multiple containers, descriptions, and fields.</Typography>
              </p>

              <p>
                <Typography>
                  <b>Keys</b> for fields must be unique.
                </Typography>
                <Typography>
                  Keys should only contain lowercase letters, numbers, and underscores (<code>_</code>).
                </Typography>
                <Typography>
                  Keep keys short, but descriptive enough to understand the field’s purpose without needing the rest of
                  the form.
                </Typography>
              </p>

              <p>
                <Typography>
                  Use the buttons below to copy or paste the form’s content in <b>YAML</b> format.
                </Typography>
              </p>

              <p>
                <Typography>
                  For more information, in particular the details on the YAML format or requirements for support staff
                  activity forms, please see the{' '}
                  <a href="https://docs.google.com/document/d/1joS4EF6RcyEcPYX7m73irtBQ6d0okUV7sJIEWxALl8Y/edit?tab=t.0#heading=h.mhy9pgbgj7c3">
                    documentation
                  </a>
                  .
                </Typography>
              </p>
            </Alert>
          )}

          {onChange && (
            <Stack direction="row" spacing={2} sx={{ my: 1 }}>
              <Button variant="outlined" size="small" onClick={handleCopyToClipboard}>
                Copy YAML
              </Button>
              <Button variant="outlined" size="small" onClick={handlePasteFromClipboard}>
                Paste YAML
              </Button>
            </Stack>
          )}

          <Stack spacing={2} sx={{ overflowX: 'auto' }}>
            {onChange && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleInsertPageAtStart()}
                sx={{ alignSelf: 'center' }}
              >
                Insert Page
              </Button>
            )}

            {pages.asJsReadonlyArrayView().map((page) => {
              const otherPages: readonly NextOption[] = pages
                .asJsReadonlyArrayView()
                .filter((localPage) => localPage.key !== page.key)
                .flatMap((localPage) => {
                  const localKey = localPage.key
                  return localKey ? [{ next: { key: localKey, type: 'page' }, label: localPage.title }] : []
                })
              return (
                <OtherPagesProvider key={keyOf(page)} value={otherPages}>
                  <FormPage
                    page={page}
                    onInsertAfter={() => handleInsertPageAfter(keyOf(page))}
                    onDelete={onChange && (() => handleDeletePage(keyOf(page)))}
                    onChange={onChange && handlePageChange}
                    isExpanded={keyOf(page) === expandedPageKey}
                    onExpand={() => handleExpand(keyOf(page))}
                  />
                </OtherPagesProvider>
              )
            })}
          </Stack>
        </Stack>
      </Stack>
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
    </>
  )
}

export default FormContentInput
